import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import supabase from "./lib/supabase";
import { isAuthError } from "./lib/authError";
import { useResponsive } from "./hooks/useResponsive";
import { LANGUAGES, detectLanguage, T, TIER_CONFIG, getTiers } from "./i18n/translations.js";
import {
  buildChassisUserMessage,
  buildReFabricateUserMessage,
} from "./features/generation/prompts";
import { AuthModal } from "./features/auth/AuthModal";
import { FinishCreatingAccount } from "./features/auth/FinishCreatingAccount";
import { SettingsModal } from "./features/auth/SettingsModal";
import { SupportModal } from "./features/auth/SupportModal";
import { TokenPurchaseModal } from "./features/billing/TokenPurchaseModal";
import { WorkspaceCreateModal } from "./features/workspace/WorkspaceCreateModal";
import { ChassisHistoryModal } from "./features/history/ChassisHistoryModal";
import { AppHeader, AppFooter } from "./shared/ui/Layout";
import { BeyondProfitSelector } from "./shared/ui/BeyondProfitSelector";
import { TIER_TOKEN_COST, BP_TOKEN_COST } from "./lib/tokenCosts";
import { readAnthropicStream, parseJSONSafe } from "./lib/streamUtils";

const Page2 = lazy(() => import("./features/output/Page2"));
const AdminPanel = lazy(() => import("./features/admin/AdminPanel"));






// ─── PAGE 1 ───────────────────────────────────────────────────────────────────
function Page1({ onSubmit, lang, setLang, user, profile, onOpenAuth, onSignOut, onRefreshProfile,
  workspaces, currentWorkspace, onSwitchWorkspace, onCreateWorkspace, onOpenHistory, onBuyTokens, onOpenAdmin, onOpenSettings, onOpenSupport, popupBlocked, chassisCount = 0, recentChassisNames = [] }: any) {
  const t = (T as any)[lang];
  const tiers = getTiers(lang);
  const { isMobile, isTablet } = useResponsive();
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState("compact");
  const [beyondProfitSelections, setBeyondProfitSelections] = useState<any[]>([]);
  const [exampleIndex, setExampleIndex] = useState(0);
  const [typedExample, setTypedExample] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const typingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (focused || input) return;
    let i = 0;
    setIsTyping(true);
    setTypedExample("");
    const current = t.examples[exampleIndex % t.examples.length];
    const type = () => {
      if (i <= current.length) { setTypedExample(current.slice(0, i)); i++; typingRef.current = setTimeout(type, 42); }
      else {
        setIsTyping(false);
        typingRef.current = setTimeout(() => {
          let j = current.length;
          const erase = () => {
            if (j >= 0) { setTypedExample(current.slice(0, j)); j--; typingRef.current = setTimeout(erase, 22); }
            else setExampleIndex(p => (p + 1) % t.examples.length);
          };
          erase();
        }, 2200);
      }
    };
    typingRef.current = setTimeout(type, 600);
    return () => clearTimeout(typingRef.current ?? undefined);
  }, [exampleIndex, focused, input, lang]);

  // Mobile tier slider — computed at top level (no hooks inside conditional JSX)
  const sliderIdx = tiers.findIndex(ti => ti.id === selectedTierId);
  const sliderTier = tiers[sliderIdx] ?? tiers[0];
  const handleTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) setSelectedTierId(tiers[(sliderIdx + 1) % tiers.length].id);
    else setSelectedTierId(tiers[(sliderIdx - 1 + tiers.length) % tiers.length].id);
  };

  const selectedTier = tiers.find(ti => ti.id === selectedTierId);
  const tokenCost = (TIER_TOKEN_COST[selectedTierId] || 1) + (beyondProfitSelections.length * BP_TOKEN_COST);
  const activeBalance = currentWorkspace
    ? (currentWorkspace.token_balance || 0)
    : (profile?.token_balance || 0);
  const hasEnoughTokens = user && activeBalance >= tokenCost;

  const handleFabricateClick = () => {
    if (!input.trim()) return;
    if (!user) { onOpenAuth("signup"); return; }
    if (!hasEnoughTokens) return; // handled via inline message
    onSubmit(input.trim(), selectedTier, beyondProfitSelections);
  };

  return (
    <div style={{ fontFamily: "'Georgia', serif", minHeight: "100vh", background: "#fff", display: "flex", flexDirection: "column" }}>
      <AppHeader lang={lang} setLang={setLang} user={user} profile={profile} onOpenAuth={onOpenAuth} onSignOut={onSignOut} onRefreshProfile={onRefreshProfile}
        workspaces={workspaces} currentWorkspace={currentWorkspace} onSwitchWorkspace={onSwitchWorkspace} onCreateWorkspace={onCreateWorkspace} onOpenHistory={onOpenHistory} onOpenAdmin={onOpenAdmin} onOpenSettings={onOpenSettings} onOpenSupport={onOpenSupport} chassisCount={chassisCount} recentChassisNames={recentChassisNames} />
      <main style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: isMobile ? "32px 20px" : isTablet ? "48px 32px" : "60px 48px" }}>
        <div style={{ width: "100%", maxWidth: 800, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ textAlign: "center", marginBottom: isMobile ? 32 : 48 }}>
            <h1 style={{ fontSize: isMobile ? 32 : isTablet ? 40 : 48, fontWeight: 900, letterSpacing: "-0.03em", color: "#000", margin: 0, lineHeight: 1.05 }}>{t.pageTitle}</h1>
          </div>

          {/* Tier Cards */}
          <div style={{ width: "100%", marginBottom: 24 }}>
            {isMobile ? (
              /* ── Mobile slider — no IIFE, all logic lives at Page1 top-level ── */
              <div style={{ width: "100%" }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}>
                <div style={{ padding: "14px 12px", background: "#000", color: "#fff", border: "2px solid #000", borderRadius: 2, textAlign: "left", height: 148, boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                    <div style={{ fontFamily: "'Courier New', monospace", fontWeight: 900, fontSize: 12, letterSpacing: "0.1em" }}>{sliderTier.label.toUpperCase()}</div>
                    <div style={{ fontFamily: "'Courier New', monospace", fontSize: 9, fontWeight: 700, color: "#888", letterSpacing: "0.06em" }}>{sliderIdx + 1} / {tiers.length}</div>
                  </div>
                  <div style={{ fontFamily: "'Courier New', monospace", fontSize: 10, fontWeight: 700, color: "#888", letterSpacing: "0.06em", marginBottom: 8 }}>
                    {TIER_TOKEN_COST[sliderTier.id]} {TIER_TOKEN_COST[sliderTier.id] === 1 ? "token" : "tokens"}
                  </div>
                  <div style={{ fontSize: 11, lineHeight: 1.6, color: "#ccc", fontFamily: "'Georgia', serif", flex: 1, overflow: "hidden" }}>{sliderTier.description}</div>
                  {/* Dot indicators */}
                  <div style={{ display: "flex", gap: 5, marginTop: 10 }}>
                    {tiers.map((_, i) => (
                      <button key={i} onClick={() => setSelectedTierId(tiers[i].id)}
                        style={{ width: i === sliderIdx ? 16 : 6, height: 6, borderRadius: 3, background: i === sliderIdx ? "#fff" : "#555", border: "none", cursor: "pointer", padding: 0, transition: "all 0.2s" }} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* ── Desktop grid ── */
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                {tiers.map(tier => {
                  const isSel = selectedTierId === tier.id;
                  return (
                    <button key={tier.id} onClick={() => setSelectedTierId(tier.id)}
                      style={{ padding: "12px 12px", background: isSel ? "#000" : "#fff", color: isSel ? "#fff" : "#000", border: isSel ? "2px solid #000" : "2px solid #d8d8d8", borderRadius: 2, cursor: "pointer", textAlign: "left", transition: "all 0.15s ease" }}>
                      <div style={{ fontFamily: "'Courier New', monospace", fontWeight: 900, fontSize: 12, letterSpacing: "0.1em", marginBottom: 3 }}>{tier.label.toUpperCase()}</div>
                      <div style={{ fontFamily: "'Courier New', monospace", fontSize: 10, fontWeight: 700, color: isSel ? "#888" : "#aaa", letterSpacing: "0.06em", marginBottom: 6 }}>
                        {TIER_TOKEN_COST[tier.id]} {TIER_TOKEN_COST[tier.id] === 1 ? "token" : "tokens"}
                      </div>
                      <div style={{ fontSize: 11, lineHeight: 1.6, color: isSel ? "#ccc" : "#666", fontFamily: "'Georgia', serif" }}>{tier.description}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Input Instruction */}
          <div style={{ width: "100%", textAlign: "center", marginBottom: 14 }}>
            <p style={{ fontFamily: "'Courier New', monospace", fontSize: isMobile ? 10 : 12, color: "#888", letterSpacing: "0.06em", margin: 0, lineHeight: 1.8, whiteSpace: isMobile ? "normal" : "pre-line" }}>{t.inputInstruction}</p>
          </div>

          {/* Textarea */}
          <div style={{ width: "100%", marginBottom: 16 }}>
            <div style={{ position: "relative", border: focused ? "2px solid #000" : "2px solid #d0d0d0", borderRadius: 2, transition: "border-color 0.2s" }}>
              {!input && !focused && (
                <div style={{ position: "absolute", top: 20, left: 20, fontFamily: "'Courier New', monospace", fontSize: 14, color: "#bbb", pointerEvents: "none", zIndex: 1 }}>
                  {typedExample}{isTyping && <span style={{ borderRight: "2px solid #bbb", marginLeft: 1 }}>&nbsp;</span>}
                </div>
              )}
              <textarea value={input} onChange={e => setInput(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); handleFabricateClick(); } }}
                rows={4} style={{ width: "100%", padding: "18px 20px", fontFamily: "'Georgia', serif", fontSize: 15, color: "#000", border: "none", outline: "none", resize: "none", background: "transparent", lineHeight: 1.7, boxSizing: "border-box", position: "relative", zIndex: 2 }} />
            </div>
          </div>


          {/* Beyond Profit */}
          <BeyondProfitSelector
            t={t}
            beyondProfitSelections={beyondProfitSelections}
            setBeyondProfitSelections={setBeyondProfitSelections}
            isMobile={isMobile}
          />

          {/* ── Popup blocked notice ── */}
          {popupBlocked && (
            <div style={{ background: "#fffbe6", border: "1px solid #f0c040", padding: "10px 16px",
              fontFamily: "'Courier New', monospace", fontSize: 11, color: "#7a5c00",
              marginBottom: 12, borderRadius: 2, letterSpacing: "0.04em" }}>
              POPUP BLOCKED — Generating in this tab instead.
            </div>
          )}

          {/* ── START FABRICATING button ── */}
          <button onClick={handleFabricateClick}
            disabled={!input.trim() || (user && !hasEnoughTokens)}
            style={{
              width: "100%", padding: isMobile ? "14px 16px" : "16px 32px", border: "none", borderRadius: 2,
              fontFamily: "'Courier New', monospace", fontSize: isMobile ? 11 : 13, fontWeight: 900,
              letterSpacing: isMobile ? "0.08em" : "0.15em", transition: "all 0.2s", whiteSpace: "nowrap",
              background: !input.trim() ? "#e8e8e8" : !user ? "#000" : hasEnoughTokens ? "#000" : "#e8e8e8",
              color: !input.trim() ? "#aaa" : !user ? "#fff" : hasEnoughTokens ? "#fff" : "#aaa",
              cursor: !input.trim() || (user && !hasEnoughTokens) ? "not-allowed" : "pointer",
            }}>
            {!user
              ? `${t.useTokens || "USE"} ${tokenCost} TOKENS & ${t.startBtn}`
              : !hasEnoughTokens
                ? `${t.insufficientTokens || 'INSUFFICIENT TOKENS'} — ${tokenCost % 1 === 0 ? tokenCost : tokenCost.toFixed(2)} ${t.required || 'REQUIRED'}`
                : `${t.useTokens || "USE"} ${tokenCost} TOKENS & ${t.startBtn}`}
          </button>

          {/* ── Auth / Token nudge messages ── */}
          {!user && input.trim() && (
            <div style={{ width: "100%", marginTop: 12, padding: "14px 16px",
              background: "#f8f8f8", border: "1px solid #e0e0e0", borderRadius: 2,
              display: "flex", flexDirection: "row", alignItems: "center",
              justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontFamily: "'Courier New', monospace", fontSize: 10, color: "#666",
                letterSpacing: "0.06em" }}>{t.auth?.freeTokens}</span>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button onClick={() => onOpenAuth("signup")}
                  style={{ padding: "8px 14px", background: "#000", color: "#fff", border: "none",
                    fontFamily: "'Courier New', monospace", fontSize: 10, fontWeight: 900,
                    letterSpacing: "0.08em", cursor: "pointer", whiteSpace: "nowrap", borderRadius: 2 }}>
                  {t.auth?.signUpTab}
                </button>
                <button onClick={() => onOpenAuth("signin")}
                  style={{ padding: "8px 14px", background: "#fff", color: "#000",
                    border: "1px solid #000", fontFamily: "'Courier New', monospace", fontSize: 10,
                    fontWeight: 700, letterSpacing: "0.08em", cursor: "pointer", whiteSpace: "nowrap", borderRadius: 2 }}>
                  {t.auth?.signInTab}
                </button>
              </div>
            </div>
          )}

          {user && !hasEnoughTokens && input.trim() && (
            <div style={{ width: "100%", marginTop: 12, padding: "16px 20px",
              background: "#f8f8f8", border: "1px solid #e0e0e0", borderRadius: 2,
              display: "flex", flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontFamily: "'Courier New', monospace", fontSize: 11, fontWeight: 900,
                  color: "#000", letterSpacing: "0.08em", marginBottom: 4 }}>
                  NOT ENOUGH TOKENS
                </div>
                <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: "#666", lineHeight: 1.6 }}>
                  You have <strong style={{ color: "#000" }}>
                    {typeof activeBalance === "number" ? (activeBalance % 1 === 0 ? activeBalance : activeBalance.toFixed(2)) : 0} tokens
                  </strong> in your {currentWorkspace ? `${currentWorkspace.name} workspace` : "account"}.
                  {" "}This tier requires <strong style={{ color: "#000" }}>
                    {tokenCost % 1 === 0 ? tokenCost : tokenCost.toFixed(2)} tokens
                  </strong>.
                </div>
              </div>
              <button
                onClick={onBuyTokens}
                style={{ padding: "9px 18px", background: "#000", color: "#fff", border: "none",
                  fontFamily: "'Courier New', monospace", fontSize: 11, fontWeight: 900,
                  letterSpacing: "0.1em", cursor: "pointer", whiteSpace: "nowrap",
                  flexShrink: 0, borderRadius: 2 }}>
                BUY TOKENS
              </button>
            </div>
          )}

          <div style={{ marginTop: 72, maxWidth: 620, textAlign: "center", borderTop: "1px solid #e8e8e8", paddingTop: 48 }}>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 10, letterSpacing: "0.2em", color: "#888", marginBottom: 20 }}>{t.whatIsChs}</div>
            <p style={{ fontSize: 14, lineHeight: 1.9, color: "#444", margin: "0 0 20px" }}>{t.chsDescription}</p>
            <p style={{ fontSize: 13, lineHeight: 1.8, color: "#555", margin: "0 0 12px" }}>
              {t.chsCreditBoss.prefix}<strong style={{ color: "#000", fontWeight: 900 }}>{t.chsCreditBoss.name}</strong>{t.chsCreditBoss.suffix}
            </p>
            <p style={{ fontSize: 13, lineHeight: 1.8, color: "#555", margin: 0 }}>
              {t.chsCreditFederico.prefix}<strong style={{ color: "#000", fontWeight: 900 }}>{t.chsCreditFederico.name}</strong>{t.chsCreditFederico.suffix}
            </p>
          </div>
        </div>
      </main>
      <AppFooter t={t} />
    </div>
  );
}

// ─── LOADING ──────────────────────────────────────────────────────────────────
const TIER_EXPECTED_SECONDS = { compact: 30, midsize: 50, executive: 110, luxury: 160 };

function LoadingScreen({ input, tierLabel, tierId, t, streamedChars = 0, streamPreview = "" }: any) {
  const { isMobile } = useResponsive();
  const isOpusTier = tierId === 'executive' || tierId === 'luxury';
  const loadingSteps = isOpusTier && t.complianceLoadingStep
    ? [...t.loadingSteps, t.complianceLoadingStep]
    : t.loadingSteps;
  const [step, setStep] = useState(0);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [elapsedMs, setElapsedMs] = useState(0);
  const startedAtRef = useRef(Date.now());
  const streaming = streamedChars > 0;
  const expectedSec = (TIER_EXPECTED_SECONDS as any)[tierId] || 60;

  useEffect(() => {
    // Advance steps until streaming starts, then jump to last step
    if (streaming) {
      setStep(loadingSteps.length - 1);
      return;
    }
    const iv = setInterval(() => setStep(p => p < loadingSteps.length - 1 ? p + 1 : p), 900);
    return () => clearInterval(iv);
  }, [streaming, loadingSteps.length]);

  // Tick elapsed time every 250ms — drives the time-based progress curve
  useEffect(() => {
    const iv = setInterval(() => setElapsedMs(Date.now() - startedAtRef.current), 250);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setPhraseIdx(p => (p + 1) % t.charmingPhrases.length);
      setDisplayed("");
    }, 12000);
    return () => clearInterval(iv);
  }, [t.charmingPhrases.length]);

  useEffect(() => {
    const phrase = t.charmingPhrases[phraseIdx] ?? "";
    if (displayed.length >= phrase.length) return;
    const tv = setTimeout(
      () => setDisplayed(phrase.slice(0, displayed.length + 1)),
      35
    );
    return () => clearTimeout(tv);
  }, [displayed, phraseIdx, t.charmingPhrases]);

  // Time-based asymptotic progress: 1 - e^(-t/τ) where τ = expectedSec/3.
  // Reaches ~95% at expectedSec, never crosses 99%, advances continuously
  // whether or not stream chars arrive.
  const elapsedSec = elapsedMs / 1000;
  const tau = expectedSec / 3;
  const timeProgress = 99 * (1 - Math.exp(-elapsedSec / tau));
  // Stream chars also push progress forward — once we have ~half the expected
  // output (assume ~3.5 chars/token, expected output ~tier.tokens * 0.5) we
  // consider it >50% done. Take whichever is higher between time and stream.
  const streamProgress = streaming
    ? Math.min(99, (streamedChars / 30000) * 99)
    : 0;
  const progress = Math.round(Math.max(timeProgress, streamProgress));

  return (
    <div style={{ minHeight: "100vh", background: "#000", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: isMobile ? "32px 16px" : "56px 20px" }}>
      <div style={{ textAlign: "center", maxWidth: isMobile ? "100%" : 520 }}>
        <img
          src="/logo.png"
          alt="CHASS1S"
          style={{
            width: isMobile ? 72 : 96,
            height: "auto",
            display: "block",
            margin: "0 auto 24px",
            opacity: 0.9,
          }}
        />
        <div style={{ fontFamily: "'Courier New', monospace", fontSize: isMobile ? 10 : 13, color: "#555", letterSpacing: "0.18em", marginBottom: 20 }}>{t.tagline}</div>
        {tierLabel && (
          <div style={{ fontFamily: "'Courier New', monospace", fontSize: isMobile ? 12 : 15, color: "#fff", letterSpacing: "0.12em", marginBottom: 28, fontWeight: 700 }}>
            {t.fabricatingPrefix} {tierLabel.toUpperCase()} CHASS1S
          </div>
        )}
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: "#fff", marginBottom: 36, lineHeight: 1.5, fontStyle: "italic" }}>"{input}"</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start", maxWidth: 360, margin: "0 auto" }}>
          {loadingSteps.map((s: any, i: number) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, opacity: i <= step ? 1 : 0.2, transition: "opacity 0.4s" }}>
              <span style={{ fontFamily: "monospace", color: i < step ? "#fff" : i === step ? "#888" : "#333", fontSize: 12 }}>{i < step ? "✓" : i === step ? "›" : "·"}</span>
              <span style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: i <= step ? "#fff" : "#333", letterSpacing: "0.08em" }}>{s}</span>
            </div>
          ))}
        </div>


        {!streaming && (
          <div style={{ marginTop: 32, display: "flex", gap: 6, justifyContent: "center" }}>
            {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", opacity: 0.3, animation: `pulse 1.2s ease-in-out ${i*0.2}s infinite` }} />)}
          </div>
        )}

        {/* ── Charming Phrases ─────────────────────────────────── */}
        <div style={{ maxWidth: isMobile ? "100%" : 400, margin: "40px auto 0", textAlign: "center" }}>
          <div style={{
            fontFamily:    "'Georgia', serif",
            fontSize:      isMobile ? 13 : 15,
            color:         "#555",
            fontStyle:     "italic",
            lineHeight:    1.8,
            minHeight:     52,
            letterSpacing: "0.01em",
          }}>
            {displayed}
            <span style={{ opacity: 0.4, animation: "pulse 1s ease-in-out infinite" }}>|</span>
          </div>
        </div>

        {/* ── Progress Meter ────────────────────────────────────── */}
        <div style={{ maxWidth: isMobile ? "100%" : 400, margin: "28px auto 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontFamily: "'Courier New', monospace", fontSize: 9, color: "#444", letterSpacing: "0.15em" }}>PROGRESS</span>
            <span style={{ fontFamily: "'Courier New', monospace", fontSize: 9, color: "#444", letterSpacing: "0.1em" }}>{progress}%</span>
          </div>
          <div style={{ height: 2, background: "#1a1a1a", borderRadius: 1, overflow: "hidden" }}>
            <div style={{
              height:     "100%",
              width:      `${progress}%`,
              background: "#fff",
              borderRadius: 1,
              transition: "width 0.8s ease",
            }} />
          </div>
        </div>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:0.2;transform:scale(0.8)}50%{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );
}

function TunePayModal({ chassis, tier, tokenCost, balance, t, onConfirm, onCancel }: any) {
  const hasEnough = balance >= tokenCost;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.72)',
        zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center',
        padding:'24px 16px' }}>
      <div style={{ background:'#fff', width:'100%', maxWidth:480 }}>
        <div style={{ background:'#000', padding:'18px 24px', display:'flex',
            alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontFamily:"'Courier New',monospace", fontWeight:900,
              fontSize:13, letterSpacing:'0.12em', color:'#fff' }}>
            {t?.tune?.payModalTitle || 'ENTER TUNE MODE'}
          </span>
          <button onClick={onCancel} style={{ background:'none', border:'none',
              color:'#aaa', fontSize:20, cursor:'pointer' }}>×</button>
        </div>
        <div style={{ padding:'24px' }}>
          <p style={{ fontFamily:"'Georgia',serif", fontSize:14, lineHeight:1.8,
              color:'#333', margin:'0 0 16px' }}>
            {t?.tune?.payModalBody ||
              'Entering Tune mode allows you to edit, refine, and add to every element of this Chassis. ' +
              'Once paid, you can toggle Tune on and off freely until you Refabricate.'}
          </p>
          <div style={{ display:'flex', justifyContent:'space-between',
              padding:'12px 0', borderTop:'1px solid #e8e8e8', borderBottom:'1px solid #e8e8e8' }}>
            <span style={{ fontFamily:"'Courier New',monospace", fontSize:11,
                fontWeight:900, letterSpacing:'0.1em' }}>
              {tier?.label?.toUpperCase()} TUNE
            </span>
            <span style={{ fontFamily:"'Courier New',monospace", fontSize:13,
                fontWeight:900, color: hasEnough ? '#000' : '#cc0000' }}>
              {tokenCost} {tokenCost === 1 ? 'token' : 'tokens'}
            </span>
          </div>
          <p style={{ fontFamily:"'Courier New',monospace", fontSize:10,
              color: hasEnough ? '#888' : '#cc0000', marginTop:8, marginBottom:20 }}>
            {t?.tune?.balance || 'BALANCE'}: {balance} tokens
            {!hasEnough && ' — ' + (t?.insufficientTokens || 'INSUFFICIENT TOKENS')}
          </p>
          <button onClick={hasEnough ? onConfirm : undefined}
            style={{ width:'100%', padding:'14px', background: hasEnough ? '#000' : '#ccc',
              border:'none', color:'#fff', fontFamily:"'Courier New',monospace",
              fontWeight:900, fontSize:12, letterSpacing:'0.1em',
              cursor: hasEnough ? 'pointer' : 'not-allowed' }}>
            {hasEnough
              ? ((t?.tune?.payConfirm || 'USE {n} TOKENS & ENTER TUNE').replace('{n}', String(tokenCost)))
              : (t?.insufficientTokens || 'INSUFFICIENT TOKENS')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {

  // ── Detect generation / view tab ──────────────────────────────────────────
  const pendingGenId = new URLSearchParams(window.location.search).get("chassis_gen");
  const pendingViewId = new URLSearchParams(window.location.search).get("chassis_view");

  // ── Viewport meta ──────────────────────────────────────────────────────────
  useEffect(() => {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    if (!meta) { meta = document.createElement('meta') as HTMLMetaElement; meta.name = 'viewport'; document.head.appendChild(meta); }
    meta.content = 'width=device-width, initial-scale=1, maximum-scale=1';
  }, []);

  // ── Language ───────────────────────────────────────────────────────────────
  const [lang, setLangState] = useState(() => {
    if (pendingGenId) {
      try { const s = JSON.parse(localStorage.getItem(pendingGenId) || "{}"); return s.lang || localStorage.getItem("chs_lang") || detectLanguage(); }
      catch { return localStorage.getItem("chs_lang") || detectLanguage(); }
    }
    if (pendingViewId) {
      try { const s = JSON.parse(localStorage.getItem(pendingViewId) || "{}"); return s.lang || localStorage.getItem("chs_lang") || detectLanguage(); }
      catch { return localStorage.getItem("chs_lang") || detectLanguage(); }
    }
    return localStorage.getItem("chs_lang") || detectLanguage();
  });
  const setLang = (l: string) => { localStorage.setItem("chs_lang", l); setLangState(l); };

  // ── Current-view persistence (per tab, survives reload via sessionStorage) ──
  // Keeps the open Chassis restorable on F5. sessionStorage is per-tab so two
  // Chassis open in two tabs never clobber each other, and it clears on close.
  const persistView = (data: { chassisData: any; tierId: any; bpSelections: any[]; bpData: any; businessInput: string; lang: string; chassisId: string | null }) => {
    try { sessionStorage.setItem("chs_view", JSON.stringify(data)); } catch { /* quota/private mode — non-fatal */ }
  };
  const clearView = () => { try { sessionStorage.removeItem("chs_view"); } catch { /* non-fatal */ } };
  const hasStoredView = (() => { try { return !!sessionStorage.getItem("chs_view"); } catch { return false; } })();

  // ── App state ──────────────────────────────────────────────────────────────
  const [screen, setScreen] = useState(
    pendingGenId || pendingViewId ? "loading" : (hasStoredView ? "loading" : "page1")
  );
  const [userInput, setUserInput] = useState("");
  const [selectedTier, setSelectedTier] = useState<any>(null);
  const [chassisData, setChassisData] = useState<any>(null);
  const [beyondProfitSelections, setBeyondProfitSelections] = useState<any[]>([]);
  const [beyondProfitData, setBeyondProfitData] = useState<any>(null);
  const [savedChassisId, setSavedChassisId] = useState<string|null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [streamedChars, setStreamedChars] = useState(0);
  const [streamPreview, setStreamPreview] = useState("");
  const [oauthError, setOauthError] = useState(() => {
    // Pick up any OAuth error stored by handleOAuthCallback() on redirect
    try {
      const msg = sessionStorage.getItem("chs_oauth_error");
      if (msg) { sessionStorage.removeItem("chs_oauth_error"); return msg; }
    } catch {}
    return null;
  });

  // ── Auth state ─────────────────────────────────────────────────────────────
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const userRef = useRef<any>(null);
  const profileRef = useRef<any>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("signin");

  // ── Workspace state ────────────────────────────────────────────────────────
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<any>(null);
  const [chassisCount, setChassisCount] = useState(0);
  const [recentChassisNames, setRecentChassisNames] = useState<string[]>([]);
  const [workspaceCreateOpen, setWorkspaceCreateOpen] = useState(false);

  // ── History state ──────────────────────────────────────────────────────────
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyOpenChassis, setHistoryOpenChassis] = useState<any>(null);

  // ── Tune mode state ────────────────────────────────────────────────────────
  const [isTuneMode, setIsTuneMode] = useState(false);
  const [tuneSessionPaid, setTuneSessionPaid] = useState(false);
  const [tunePayModalOpen, setTunePayModalOpen] = useState(false);

  // Reset Tune state on chassis change, then check if a paid session
  // already exists server-side for this chassis (persisted across
  // refresh, tab close, and multi-device).
  useEffect(() => {
    setIsTuneMode(false);
    setTuneSessionPaid(false);
    if (!savedChassisId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('tune_sessions')
        .select('chassis_id')
        .eq('chassis_id', savedChassisId)
        .limit(1);
      if (cancelled) return;
      if (Array.isArray(data) && data.length > 0) setTuneSessionPaid(true);
    })();
    return () => { cancelled = true; };
  }, [savedChassisId]);

  const handleTuneToggle = () => {
    if (isTuneMode) { setIsTuneMode(false); return; }
    if (tuneSessionPaid) { setIsTuneMode(true); return; }
    setTunePayModalOpen(true);
  };

  const openAuth = (mode = "signin") => { setAuthModalMode(mode); setAuthModalOpen(true); };

  const t = (T as any)[lang];

  // ── Fetch user profile ─────────────────────────────────────────────────────
  const fetchProfile = async (userId: string) => {
    const { data } = await supabase.from("profiles").select("*").eq("id", userId).single();
    if (data) { setProfile(data); profileRef.current = data; }
    return data;
  };

  // ── Fetch user workspaces ──────────────────────────────────────────────────
  const fetchWorkspaces = async (userId: string) => {
    const { data: rawMemberships } = await supabase.from("workspace_members").select("*").eq("user_id", userId);
    const memberships = rawMemberships as any[] | null;
    if (!memberships?.length) { setWorkspaces([]); return; }
    const wsResults = await Promise.all(memberships.map((m: any) => supabase.from("workspaces").select("*").eq("id", m.workspace_id).single()));
    const combined = wsResults.map((r, i) => (r as any).data ? { ...(r as any).data, role: memberships[i].role } : null).filter(Boolean);
    setWorkspaces(combined);
  };

  // ── Fetch chassis history count + last 3 business names ─────────────────
  const fetchChassisCount = async (userId: string, workspaceId: string | null = null) => {
    try {
      const addFilters = (q: any) => {
        // In a workspace, count every member's chassis (RLS scopes to members).
        // In Personal, restrict to this user's own rows.
        if (workspaceId !== null) q.eq("workspace_id", workspaceId);
        else q.eq("user_id", userId).is("workspace_id", null);
        return q;
      };

      const countQuery = addFilters(supabase.from("chassis_history").select("id"));
      const namesQuery = addFilters(supabase.from("chassis_history").select("business_name"));
      namesQuery.order("created_at", { ascending: false }).limit(3);

      const [{ count }, { data: namesData }] = await Promise.all([
        countQuery.count(),
        namesQuery,
      ]);

      // Only update state on a successful response — never reset to 0 on error
      if (count !== null) setChassisCount(count);
      if (Array.isArray(namesData)) {
        setRecentChassisNames((namesData as any[]).map((r) => r.business_name).filter(Boolean));
      }
    } catch {
      // Network/auth error — preserve existing count rather than showing 0
    }
  };

  // ── Auth session listener ─────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { const session = (data as any)?.session;
      if (session?.user) { setUser(session.user); userRef.current = session.user; fetchProfile(session.user.id); fetchWorkspaces(session.user.id); fetchChassisCount(session.user.id); }
    });
    const { data: authData } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u); userRef.current = u;
      if (u) { fetchProfile(u.id).then(async (prof) => { if (prof && (prof as any).onboarding_complete === false) setScreen('onboarding'); if (prof && (prof as any).account_status === 'deactivated') { await supabase.rpc('reactivate_account', { p_user_id: u.id }); fetchProfile(u.id); } }); fetchWorkspaces(u.id); fetchChassisCount(u.id); }
      else { setProfile(null); setWorkspaces([]); setCurrentWorkspace(null); }
    });
    return () => (authData as any).subscription.unsubscribe();
  }, []);

  // ── Refresh chassis count on tab focus (fixes stale menu after idle) ────────
  useEffect(() => {
    const handleVisible = async () => {
      if (document.visibilityState !== "visible") return;
      // Re-check session to trigger token refresh if JWT expired while away
      const { data: _visData } = await supabase.auth.getSession();
      const session = (_visData as any)?.session;
      const u = session?.user ?? user;
      if (u) fetchChassisCount(u.id, currentWorkspace?.id ?? null);
    };
    document.addEventListener("visibilitychange", handleVisible);
    return () => document.removeEventListener("visibilitychange", handleVisible);
  }, [user, currentWorkspace]);

  // ── Handle return from Stripe payment ─────────────────────────────────────
  // Tokens are credited server-side via the Stripe webhook (api/stripe-webhook.js).
  // On return we refresh both the profile and workspace balances — a purchase
  // may have credited either one, and after the Stripe redirect the app has
  // reloaded so workspace balances need to be re-fetched from DB.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") !== "success" || !user) return;
    window.history.replaceState(null, "", window.location.pathname);
    fetchProfile(user.id);
    fetchWorkspaces(user.id);
  }, [user]);

  // ── Sign out ───────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    clearView();
    setUser(null); setProfile(null); setWorkspaces([]); setCurrentWorkspace(null);
    setScreen("page1"); setChassisData(null); setSavedChassisId(null);
  };

  // ── Atomic: deduct tokens AND insert history row in a single transaction ───
  // Backed by the consume_and_save_chassis RPC (migration 003). Either both
  // sides land or neither does, so the user can never pay tokens without a
  // history record being persisted.
  const consumeAndSave = async (parsed: any, tier: any, input: any, bpSelections: any, currentLang: any, actionType = 'fabricated', parentChassisId: string | null = null, workspaceIdOverride?: string | null) => {
    const u = userRef.current;
    const p = profileRef.current;
    if (!u) return { ok: false, reason: "no_user" };
    // Prefer an explicit workspace id (threaded through the new-tab generation
    // flow, where currentWorkspace state is always null). Fall back to state for
    // same-tab paths (re-fabricate, popup-blocked). undefined = not provided.
    const wsId = workspaceIdOverride !== undefined ? workspaceIdOverride : (currentWorkspace?.id ?? null);

    // ── Re-fabrication: update the existing chassis in place ─────────────────
    // Instead of inserting a new (child) history row, refabricate_chassis
    // charges tokens and UPDATEs the row identified by parentChassisId — so a
    // tier change (e.g. Compact → Executive) modifies the same chassis rather
    // than creating a new one. The RPC (SECURITY DEFINER) authorizes the caller
    // as owner or workspace member and charges the chassis's own balance.
    if (actionType === 'refabricated' && parentChassisId) {
      const { data: rfData, error: rfErr } = await supabase.rpc("refabricate_chassis", {
        p_user_id: u.id,
        p_chassis_id: parentChassisId,
        p_business_name: parsed.business?.name || input,
        p_business_input: input,
        p_tier: tier.id,
        p_lang: currentLang,
        p_chassis_data: parsed,
        p_beyond_profit_selections: bpSelections,
      });

      if (rfErr) {
        console.error("refabricate_chassis RPC error:", rfErr.message);
        return { ok: false, reason: "rpc_error", message: rfErr.message };
      }

      const rfRow = Array.isArray(rfData) ? rfData[0] : rfData;
      if (!rfRow || rfRow.status !== "ok") {
        return { ok: false, reason: rfRow?.status || "unknown" };
      }

      // Refresh the balance that was charged (workspace or personal).
      if (wsId) {
        const { data: ws } = await supabase.from("workspaces").select("*").eq("id", wsId).single();
        if (ws) setCurrentWorkspace(ws);
      } else if (p) {
        const { data: prof } = await supabase.from("profiles").select("*").eq("id", u.id).single();
        if (prof) { setProfile(prof); profileRef.current = prof; }
      }

      fetchChassisCount(u.id, wsId);
      // Same row, same id — the chassis was updated, not replaced.
      return { ok: true, chassisId: parentChassisId };
    }

    const { data, error: rpcErr } = await supabase.rpc("consume_and_save_chassis", {
      p_user_id: u.id,
      p_workspace_id: wsId,
      p_business_name: parsed.business?.name || input,
      p_business_input: input,
      p_tier: tier.id,
      p_lang: currentLang,
      p_chassis_data: parsed,
      p_beyond_profit_selections: bpSelections,
      p_action_type: actionType,
      p_parent_chassis_id: parentChassisId ?? null,
    });

    if (rpcErr) {
      console.error("consume_and_save_chassis RPC error:", rpcErr.message);
      return { ok: false, reason: "rpc_error", message: rpcErr.message };
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row || row.status !== "ok") {
      return { ok: false, reason: row?.status || "unknown" };
    }

    // Refresh local balance from DB to stay in sync
    if (wsId) {
      const { data: ws } = await supabase.from("workspaces").select("*").eq("id", wsId).single();
      if (ws) setCurrentWorkspace(ws);
    } else if (p) {
      const { data: prof } = await supabase.from("profiles").select("*").eq("id", u.id).single();
      if (prof) { setProfile(prof); profileRef.current = prof; }
    }

    fetchChassisCount(u.id, wsId);

    // Fetch the ID of the record just inserted so Page2 can persist BP data later
    const { data: idRow } = await supabase
      .from('chassis_history')
      .select('id')
      .eq('user_id', u.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    return { ok: true, chassisId: (idRow as any)?.id ?? null };
  };

  // ── Trigram cache helpers (pg_trgm — no external API) ───────────────────
  // Normalize input before storing/querying so minor formatting differences
  // (extra spaces, trailing commas, mixed case) don't create duplicate entries.
  const normalizeInput = (text: string) => text.toLowerCase().replace(/\s+/g, " ").trim();

  // Query Supabase for a similar cached chassis result (similarity > 0.65).
  // Returns { id, chassis_data } or null on miss / error.
  const lookupCache = async (input: string, tierId: string, currentLang: string) => {
    try {
      const { data: rawData, error } = await supabase.rpc("lookup_chassis_cache", {
        p_input_text: normalizeInput(input),
        p_tier_id: tierId,
        p_lang: currentLang,
        p_threshold: 0.65,
      });
      const data = rawData as any[];
      if (error || !data?.length) return null;
      return { id: data[0].id, chassis_data: data[0].chassis_data };
    } catch {
      return null;
    }
  };

  // Persist a new result to the cache via server endpoint (fire-and-forget, non-blocking).
  // Uses /api/store-cache so the DB write goes through service_role — not direct RPC.
  const storeCache = async (input: string, tierId: string, currentLang: string, chassisData: any) => {
    try {
      const { data: _cacheData } = await supabase.auth.getSession(); const cacheSession = (_cacheData as any)?.session;
      if (!cacheSession?.access_token) return;
      await fetch("/api/store-cache", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${cacheSession.access_token}`,
        },
        body: JSON.stringify({
          input_text: normalizeInput(input),
          tier_id: tierId,
          lang: currentLang,
          chassis_data: chassisData,
        }),
      });
    } catch {
      // Cache write failure is non-fatal
    }
  };

  // ── Core generation logic ─────────────────────────────────────────────────
  const runGeneration = async (input: any, tier: any, currentLang: any, bpSelections: any, existingChassis: any = null, actionType = 'fabricated', parentChassisId: string | null = null, tuneEdits: Record<string, any> | null = null, workspaceId?: string | null) => {
    try {
      const { data: _genData } = await supabase.auth.getSession(); const genSession = (_genData as any)?.session;
      const sessionToken = genSession?.access_token;

      if (!genSession?.user?.email_confirmed_at) {
        throw new Error((T as any)[currentLang]?.errorEmailNotConfirmed ?? "Please confirm your email address before generating. Check your inbox.");
      }

      // ── Trigram cache check (no external API) ──────────────────────────
      // Skip cache entirely for re-fabrications — the existing chassis must
      // be used as context by the AI, so a cached result would bypass that.
      let cachedResult = null;
      if (!existingChassis) {
        try {
          const hit = await lookupCache(input, tier.id, currentLang);
          if (hit) {
            cachedResult = hit.chassis_data;
            // Increment hit counter (fire-and-forget)
            supabase.rpc("record_cache_hit", { p_cache_id: hit.id }).catch(() => {});
          }
        } catch {
          // Cache lookup failure is non-fatal — fall through to live generation
        }
      }

      let parsed;
      if (cachedResult) {
        // ── Cache HIT: use cached result, still charge tokens ───────────
        parsed = cachedResult;
        setStreamedChars(0);
        setStreamPreview("⚡ Retrieved from cache");
      } else {
        // ── Cache MISS: call Anthropic ──────────────────────────────────
        // Use AbortController only for the initial connection (60s).
        // Once the stream starts flowing, we rely on keep-alive pings
        // from the edge function and the natural stream lifecycle —
        // aborting mid-stream would surface as 'BodyStreamBuffer was aborted'.
        const connectController = new AbortController();
        const connectTimeout = setTimeout(() => connectController.abort(), 60000);
        let res;
        try {
          res = await fetch("/api/anthropic", {
            method: "POST",
            signal: connectController.signal,
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({
              max_tokens: tier.tokens,
              mode: "chassis",
              tier: tier.key,
              messages: [{ role: "user", content: existingChassis
                ? buildReFabricateUserMessage(input, input, tier, currentLang, bpSelections, existingChassis, tuneEdits)
                : buildChassisUserMessage(input, tier, currentLang) }],
            }),
          });
        } catch (err) {
          clearTimeout(connectTimeout);
          if ((err as any).name === "AbortError") throw new Error((T as any)[currentLang]?.errorConnectionTimeout ?? "Connection timed out. Please try again.");
          throw err;
        }
        // Connection established — clear the connection timeout so it
        // does NOT abort the long-running stream read below.
        clearTimeout(connectTimeout);
        setStreamedChars(0);
        setStreamPreview("");
        const raw = await readAnthropicStream(res, (accumulated) => {
          setStreamedChars(accumulated.length);
          setStreamPreview(accumulated.slice(-120));
        });
        parsed = parseJSONSafe(raw);

        // Store in cache for future hits (fire-and-forget, non-blocking)
        storeCache(input, tier.id, currentLang, parsed).catch(() => {});
      }

      // Persist + charge atomically BEFORE rendering Page2. If this fails,
      // the user keeps their tokens and sees an error instead of a result
      // they were charged for but cannot retrieve from history.
      let result = await consumeAndSave(parsed, tier, input, bpSelections, currentLang, actionType, parentChassisId, workspaceId);

      // The stream can outlive the access token. The refresh timer in
      // lib/supabase.ts now renews it mid-stream, but this is the one call where
      // being wrong is expensive: the Chassis is finished, the API is already
      // paid for, and this RPC is what charges and stores it. Losing here means
      // throwing away work the customer will never see and we cannot recover.
      //
      // Retrying is safe. Both branches of consumeAndSave are a single
      // SECURITY DEFINER RPC, and an expired token is rejected by PostgREST
      // before the function body runs — nothing charged, nothing written, so
      // there is no partial state for a second attempt to duplicate.
      if (!result.ok && result.reason === "rpc_error" && isAuthError(result.message)) {
        await supabase.auth.refreshSession();
        result = await consumeAndSave(parsed, tier, input, bpSelections, currentLang, actionType, parentChassisId, workspaceId);
      }

      if (!result.ok) {
        const msg =
          result.reason === "insufficient_balance" ? ((T as any)[currentLang]?.errorInsufficientBalance ?? "Insufficient token balance.") :
          result.reason === "not_found" ? ((T as any)[currentLang]?.errorAccountNotFound ?? "Account not found.") :
          result.message || ((T as any)[currentLang]?.errorSaveChassis ?? "Could not save your Chassis. No tokens were charged.");
        throw new Error(msg);
      }

      setSavedChassisId(result.chassisId ?? null);
      setChassisData(parsed);
      persistView({ chassisData: parsed, tierId: tier.id, bpSelections: bpSelections || [], bpData: null, businessInput: input, lang: currentLang, chassisId: result.chassisId ?? null });
      setScreen("page2");
    } catch (err) {
      setErrorMsg(`${(err as any).message}`);
      setScreen("error");
    }
  };

  // ── Auto-generate when this is a new generation tab ───────────────────────
  useEffect(() => {
    if (!pendingGenId) return;
    const stored = localStorage.getItem(pendingGenId);
    if (!stored) { setScreen("page1"); return; }
    let params;
    try { params = JSON.parse(stored); } catch { setScreen("page1"); return; }
    localStorage.removeItem(pendingGenId);
    // Strip the one-shot ?chassis_gen= param so a reload after generation falls
    // through to the sessionStorage restore (instead of an emptied localStorage key).
    window.history.replaceState(null, "", window.location.pathname);
    const { input, tier, bpSelections, lang: storedLang, existingChassis, actionType, parentChassisId, tuneEdits, workspaceId } = params;
    setUserInput(input);
    setSelectedTier(tier);
    setBeyondProfitSelections(bpSelections || []);
    if (storedLang) setLang(storedLang);
    runGeneration(input, tier, storedLang || lang, bpSelections || [], existingChassis || null, actionType || 'fabricated', parentChassisId ?? null, tuneEdits ?? null, workspaceId ?? null);
  }, []);

  // ── Load history chassis in this view tab (no re-generation) ─────────────
  useEffect(() => {
    if (!pendingViewId) return;
    const stored = localStorage.getItem(pendingViewId);
    localStorage.removeItem(pendingViewId);
    if (!stored) { setScreen("page1"); return; }
    let params;
    try { params = JSON.parse(stored); } catch { setScreen("page1"); return; }
    const { chassisData, tierId, beyondProfitSelections: bpSels, beyondProfitData: bpData, businessInput, lang: storedLang, chassisId: storedChassisId } = params;
    if (!chassisData) { setScreen("page1"); return; }
    if (storedLang) setLang(storedLang);
    if (storedChassisId) setSavedChassisId(storedChassisId);
    setChassisData(chassisData);
    setSelectedTier(getTiers(storedLang || lang).find(t => t.id === tierId) || getTiers(lang)[0]);
    setBeyondProfitSelections(bpSels || []);
    if (bpData) setBeyondProfitData(bpData);
    setUserInput(businessInput || "");
    persistView({ chassisData, tierId, bpSelections: bpSels || [], bpData: bpData || null, businessInput: businessInput || "", lang: storedLang || lang, chassisId: storedChassisId || null });
    // Clean the one-shot param so subsequent reloads use sessionStorage.
    window.history.replaceState(null, "", window.location.pathname);
    setScreen("page2");
  }, []);

  // ── Restore the open Chassis on a normal reload (per-tab sessionStorage) ───
  useEffect(() => {
    if (pendingGenId || pendingViewId) return; // those flows own the restore
    let stored: string | null = null;
    try { stored = sessionStorage.getItem("chs_view"); } catch { stored = null; }
    if (!stored) { if (hasStoredView) setScreen("page1"); return; }
    let params;
    try { params = JSON.parse(stored); } catch { setScreen("page1"); return; }
    const { chassisData: cd, tierId, beyondProfitSelections: bpSels, beyondProfitData: bpData, businessInput, lang: storedLang, chassisId: storedChassisId } = params;
    if (!cd) { setScreen("page1"); return; }
    if (storedLang) setLang(storedLang);
    if (storedChassisId) setSavedChassisId(storedChassisId);
    setChassisData(cd);
    setSelectedTier(getTiers(storedLang || lang).find(t => t.id === tierId) || getTiers(lang)[0]);
    setBeyondProfitSelections(bpSels || []);
    if (bpData) setBeyondProfitData(bpData);
    setUserInput(businessInput || "");
    setScreen("page2");
  }, []);

  // ── openChassisInNewTab — opens a history result in a new tab ─────────────
  const openChassisInNewTab = (ch: any) => {
    const viewId = "chassis_view_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
    localStorage.setItem(viewId, JSON.stringify({
      chassisData: ch.chassis_data,
      tierId: ch.tier,
      beyondProfitSelections: ch.beyond_profit_selections || [],
      beyondProfitData: ch.beyond_profit_data || null,
      businessInput: ch.business_input || "",
      lang: ch.lang || lang,
      chassisId: ch.id || null,
    }));
    const url = window.location.origin + window.location.pathname + "?chassis_view=" + viewId;
    const newTab = window.open(url, "_blank");
    if (!newTab) {
      localStorage.removeItem(viewId);
      if (ch.chassis_data) {
        setSavedChassisId(ch.id || null);
        setChassisData(ch.chassis_data);
        setSelectedTier(getTiers(lang).find(t => t.id === ch.tier) || getTiers(lang)[0]);
        setBeyondProfitSelections(ch.beyond_profit_selections || []);
        setBeyondProfitData(ch.beyond_profit_data ?? null);
        setUserInput(ch.business_input || "");
        persistView({ chassisData: ch.chassis_data, tierId: ch.tier, bpSelections: ch.beyond_profit_selections || [], bpData: ch.beyond_profit_data ?? null, businessInput: ch.business_input || "", lang: ch.lang || lang, chassisId: ch.id || null });
        setScreen("page2");
      }
    }
  };

  // ── generateChassis — opens new tab, Page 1 stays ─────────────────────────
  const generateChassis = (input: any, tier: any, bpSelections: any[] = []) => {
    const genId = "chassis_gen_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
    // Carry the active workspace into the new generation tab — that tab boots
    // with a fresh (null) currentWorkspace and would otherwise charge personal.
    localStorage.setItem(genId, JSON.stringify({ input, tier, bpSelections, lang, workspaceId: currentWorkspace?.id ?? null }));
    const newTabUrl = window.location.origin + window.location.pathname + "?chassis_gen=" + genId;
    const newTab = window.open(newTabUrl, "_blank");
    if (!newTab) {
      localStorage.removeItem(genId);
      setPopupBlocked(true);
      setTimeout(() => setPopupBlocked(false), 5000);
      setUserInput(input); setSelectedTier(tier);
      setBeyondProfitSelections(bpSelections); setBeyondProfitData(null);
      setScreen("loading");
      runGeneration(input, tier, lang, bpSelections);
    }
  };

  // ── Re-fabricate chassis — opens new tab with existing chassis as context ──
  const reFabricateChassis = (newInput: any, tier: any, bpSelections: any, existingChassisData: any, parentChassisId: string | null = null, tuneEdits: Record<string, any> | null = null) => {
    setIsTuneMode(false);
    setTuneSessionPaid(false);
    // Refabricate in the SAME tab — the user already has the current chassis
    // open and expects it to be replaced, not duplicated in a new tab.
    setUserInput(newInput);
    setSelectedTier(tier);
    setBeyondProfitSelections(bpSelections);
    setBeyondProfitData(null);
    setChassisData(null);
    setScreen('loading');
    runGeneration(newInput, tier, lang, bpSelections, existingChassisData, 'refabricated', parentChassisId, tuneEdits);
  };

  // ── Reset to page1 ─────────────────────────────────────────────────────────
  const resetToPage1 = () => {
    clearView();
    setScreen("page1"); setChassisData(null); setUserInput(""); setSavedChassisId(null);
    setBeyondProfitSelections([]); setBeyondProfitData(null); setErrorMsg("");
    window.history.replaceState(null, "", window.location.pathname);
  };

  // Atomic: deducts tokens AND opens a tune_sessions row in one RPC.
  // The RPC short-circuits with 'already_open' if a session already
  // exists for this chassis (avoids double-charging on refresh / race).
  const handleTunePayConfirm = async () => {
    if (!savedChassisId) return;
    const { error } = await supabase.rpc('start_tune_session', {
      p_chassis_id:   savedChassisId,
      p_workspace_id: currentWorkspace?.id ?? null,
    });
    if (error) return;
    setTuneSessionPaid(true);
    setIsTuneMode(true);
    setTunePayModalOpen(false);
    if (user) fetchProfile(user.id);
  };

  const [buyTokensOpen, setBuyTokensOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const authProps = {
    user, profile,
    onOpenAuth: openAuth,
    onSignOut: handleSignOut,
    onRefreshProfile: () => user && fetchProfile(user.id),
    workspaces,
    currentWorkspace,
    onSwitchWorkspace: (ws: any) => { setCurrentWorkspace(ws); if (user) fetchChassisCount(user.id, ws?.id ?? null); },
    onCreateWorkspace: () => setWorkspaceCreateOpen(true),
    onWorkspaceDeleted: (wsId: string) => setWorkspaces(prev => prev.filter((w: any) => w.id !== wsId)),
    onOpenHistory: () => setHistoryOpen(true),
    onBuyTokens: () => setBuyTokensOpen(true),
    onOpenAdmin: profile?.role === "admin" ? () => setScreen("admin") : undefined,
    onOpenSettings: () => setSettingsOpen(true),
    onOpenSupport: () => setSupportOpen(true),
    chassisCount,
    recentChassisNames,
  };

  // A VALUE, not a component, and deliberately so.
  //
  // This was `const Modals = () => (...)` rendered as `<Modals />`. Defining a
  // component during render gives it a new function identity every time, so
  // React sees a different type at that position and unmounts the whole subtree
  // — every modal loses its state on any parent re-render.
  //
  // That is not theoretical here. The auth listener calls setUser, setProfile,
  // setWorkspaces and setChassisCount on every token refresh, and a refresh can
  // land mid-interaction: a support ticket submitted successfully came back as a
  // blank form, because the modal was destroyed and rebuilt between the insert
  // and the confirmation screen. The user sees no ticket number and submits
  // again.
  //
  // OAuthErrorToast right below is already written this way. Keep both as
  // values; do not turn this back into a component.
  const modals = (
    <>
      {authModalOpen && <AuthModal onClose={() => setAuthModalOpen(false)} onSuccess={setUser} initialMode={authModalMode} lang={lang} />}
      {workspaceCreateOpen && <WorkspaceCreateModal user={user} onClose={() => setWorkspaceCreateOpen(false)} onCreated={(ws: any) => { setWorkspaces(prev => [...prev, ws]); setWorkspaceCreateOpen(false); }}
        lang={lang} t={(T as any)[lang]} />}
      {buyTokensOpen && user && <TokenPurchaseModal user={user} profile={profile} currentWorkspace={currentWorkspace} onClose={() => setBuyTokensOpen(false)} onTokensAdded={() => { fetchProfile(user.id); setBuyTokensOpen(false); }}
        lang={lang} t={(T as any)[lang]} />}
      {settingsOpen && user && <SettingsModal user={user} profile={profile} lang={lang} t={(T as any)[lang]} onClose={() => setSettingsOpen(false)} onSignOut={handleSignOut} onRefreshProfile={() => fetchProfile(user.id)} />}
      {supportOpen && user && <SupportModal user={user} profile={profile} lang={lang} t={(T as any)[lang]} onClose={() => setSupportOpen(false)} />}
      {historyOpen && user && <ChassisHistoryModal user={user} currentWorkspace={currentWorkspace} onClose={() => setHistoryOpen(false)}
        onOpenChassis={(ch: any) => { openChassisInNewTab(ch); setHistoryOpen(false); }}
        lang={lang} t={(T as any)[lang]} />}
      {tunePayModalOpen && (
        <TunePayModal
          chassis={chassisData}
          tier={selectedTier}
          tokenCost={TIER_TOKEN_COST[selectedTier?.id] || 0}
          balance={currentWorkspace ? (currentWorkspace.token_balance || 0) : (profile?.token_balance || 0)}
          t={(T as any)[lang]}
          onConfirm={handleTunePayConfirm}
          onCancel={() => setTunePayModalOpen(false)}
        />
      )}
    </>
  );

  // ── OAuth error toast (shown once after a failed/cancelled OAuth redirect) ──
  const OAuthErrorToast = oauthError ? (
    <div style={{
      position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
      background: "#1a1a1a", color: "#fff", padding: "12px 20px 12px 16px",
      borderRadius: 4, fontSize: 13, fontFamily: "'Courier New', monospace",
      display: "flex", alignItems: "center", gap: 12, zIndex: 9999,
      boxShadow: "0 4px 20px rgba(0,0,0,0.3)", maxWidth: "90vw",
    }}>
      <span style={{ color: "#f87171" }}>⚠</span>
      <span>{oauthError}</span>
      <button onClick={() => setOauthError(null)} style={{
        background: "none", border: "none", color: "#888", cursor: "pointer",
        fontSize: 16, lineHeight: 1, padding: "0 0 0 4px", flexShrink: 0,
      }}>×</button>
    </div>
  ) : null;

  if (screen === 'onboarding' && user) return (
    <FinishCreatingAccount user={user} lang={lang} onComplete={() => { fetchProfile(user.id); setScreen('page1'); }} />
  );

  if (screen === "page1") return (
    <>
      <Page1 onSubmit={generateChassis} lang={lang} setLang={setLang} popupBlocked={popupBlocked} {...authProps} />
      {modals}
      {OAuthErrorToast}
    </>
  );
  if (screen === "loading") return <LoadingScreen input={userInput} tierLabel={selectedTier?.label} tierId={selectedTier?.id} t={t} streamedChars={streamedChars} streamPreview={streamPreview} />;
  if (screen === "error") return (
    <>
      <div style={{ minHeight:"100vh", background:"#fff", display:"flex", flexDirection:"column", fontFamily:"'Georgia',serif" }}>
        <AppHeader lang={lang} setLang={setLang} {...authProps} />
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:48 }}>
          <div style={{ textAlign:"center", maxWidth:480 }}>
            <div style={{ fontFamily:"'Courier New',monospace", fontSize:11, color:"#888", letterSpacing:"0.2em", marginBottom:24 }}>CHASS1S · {t.tagline}</div>
            <h2 style={{ fontSize:28, fontWeight:900, color:"#000", margin:"0 0 16px" }}>{t.errorTitle}</h2>
            <p style={{ fontSize:14, color:"#555", lineHeight:1.7, margin:"0 0 32px" }}>{errorMsg}</p>
            <button onClick={resetToPage1} style={{ padding:"12px 32px", background:"#000", color:"#fff", border:"none", fontFamily:"'Courier New',monospace", fontSize:12, fontWeight:900, letterSpacing:"0.15em", cursor:"pointer" }}>{t.tryAgain}</button>
          </div>
        </div>
        <AppFooter t={t}/>
      </div>
      {modals}
    </>
  );
  if (screen === "page2" && chassisData) return (
    <>
      <Suspense fallback={null}>
        <Page2 chassisData={chassisData} tier={selectedTier} lang={lang} setLang={setLang}
          beyondProfitSelections={beyondProfitSelections} beyondProfitData={beyondProfitData}
          setBeyondProfitData={setBeyondProfitData} userInput={userInput} onNewChassis={resetToPage1}
          onReFabricate={reFabricateChassis} savedChassisId={savedChassisId}
          onChassisDeleted={resetToPage1}
          isTuneMode={isTuneMode} onTuneToggle={handleTuneToggle}
          {...authProps} />
      </Suspense>
      {modals}
    </>
  );
  if (screen === "admin" && profile?.role === "admin") return (
    <Suspense fallback={null}>
      <AdminPanel onBack={() => setScreen("page1")} lang={lang} setLang={setLang} {...authProps} />
    </Suspense>
  );
  return null;
}
